<template>
	<ViewFeedItem class="view-feed-contentgrid">
		<template v-slot:header>
			<ViewLayoutModuleHeader v-bind:title="title" v-bind:subtitle="subtitle">
				<RouterLink v-bind:to="{ name: 'PageContent' }">
					<strong>View All</strong>
				</RouterLink>
			</ViewLayoutModuleHeader>
		</template>

		<template v-slot:body>
			<ViewUiStateLoader v-if="contentCollection.length === 0" />

			<div class="grid" v-if="contentCollection.length > 0">
				<ViewContentCard
					v-bind:contentModel="contentModel"
					v-bind:key="contentModel.id"
					v-for="contentModel in contentCollection.models"
					v-on:select="Handle_OnSelectContent"
				/>
			</div>
		</template>
	</ViewFeedItem>
</template>

<script lang="ts">
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component, Prop } from 'vue-property-decorator';
	import { mounted } from '@/Utility/Decorators';

	/**
	 * Two-column grid of mixed content cards. Renders whatever collection
	 * it receives, defaulting to recent news. Useful when you want a
	 * denser, browse-friendly surface than the horizontal rows.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Feed
	 * @project ChalkySticks App
	 */
	@Component
	export default class ViewFeedContentGrid extends ViewBase {
		/**
		 * Subtitle copy beneath the module header
		 *
		 * @return string
		 */
		protected get subtitle(): string {
			return ''; // 'A spread of recent stories';
		}

		/**
		 * Title copy in the module header
		 *
		 * @return string
		 */
		protected get title(): string {
			return 'Browse The Latest';
		}

		/**
		 * Source collection of content to render in the grid
		 *
		 * @type ChalkySticks.Collection.Content
		 */
		@Prop({
			default: () => ChalkySticks.Factory.Content.collection({ limit: 6 }).news(),
		})
		public contentCollection!: ChalkySticks.Collection.Content;

		/**
		 * Fetch content on mount.
		 *
		 * @return Promise<void>
		 */
		@mounted
		public async setup(): Promise<void> {
			await this.contentCollection.fetch();
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @param ChalkySticks.Model.Content contentModel
		 * @return Promise<void>
		 */
		protected async Handle_OnSelectContent(contentModel: ChalkySticks.Model.Content): Promise<void> {
			this.$router.push({
				name: 'PageContentItem',
				params: {
					id: contentModel.getSlug(),
				},
			});
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	// Layout
	// ---------------------------------------------------------------------------

	.view-feed-contentgrid {
		.title {
			font-size: 1.2rem;
		}

		.body {
			padding: 0 var(--padding) var(--padding);
		}

		.grid {
			display: grid;
			gap: 0.75rem;
			grid-template-columns: 1fr 1fr;
		}
	}

	// Media Query
	// ---------------------------------------------------------------------------

	@include media-query('> tablet') {
		.view-feed-contentgrid .grid {
			grid-template-columns: repeat(3, 1fr);
		}
	}
</style>
